import { IkeGeodesic, IkeUnit } from '@ikegps/core-measurement';
import { IkeUnitAngle, type Point } from '@ikegps/core-api';
import { CollectionModelFieldAnchor, CollectionModelFieldVector } from '@ikegps/core-shared';
import { addStyles, rgba } from '../../../components/theme';
import {
	BLACK,
	ColorPaletteBlue,
	ColorPaletteGrey,
	ColorPaletteOrange,
	ColorPaletteYellow,
	WHITE,
} from '../../../components/theme.palette';
import { m } from '../../../mithril';
import { Services } from '../../../services';
import { uprightAngle } from '../../../components/line.symbol.render';
import { classNames } from '../../../util/classnames';
import { PointCache } from '../../google/projection.cache';
import type { PointCollectionLineInfo } from '../point.collection.cache';

interface ITopologicalEditorMapLineOptions {
	initialPoint: google.maps.Point;
	vector: PointCollectionLineInfo;
	kind: MaybeSpanType;
	isSelected: boolean;
	onClick: () => void;
	onVectorHandleMouseDown: (event: MouseEvent) => void;
	onVectorMouseOver?: () => void;
	onVectorMouseOut?: () => void;
	vectorHandleMoveDelta: Point;
	showVectorLabel: boolean;
	drawLabels: boolean;
	/** Hide the always-on label (reciprocal backspan collision); hover/selection still shows it. */
	suppressLabel: boolean;
}

export type SpanType = 'span' | 'dropspan' | 'vector-anchor' | 'anchor';

export type MaybeSpanType = SpanType | 'unknown';

export const LineCSS = {
	container: 'c-TopologicalEditorMapLine',
	vector: 'c-TopologicalEditorMapLine__vector',
	line: 'c-TopologicalEditorMapLine__line',
	highlight: 'c-TopologicalEditorMapLine__highlight',
	lineSelected: 'c-TopologicalEditorMapLine__line--selected',
	label: 'c-TopologicalEditorMapLine__label',
	labelText: 'c-TopologicalEditorMapLine__labelText',
	clickTarget: 'c-TopologicalEditorMapLine__clickTarget',
	handle: 'c-TopologicalEditorMapLine__handle',
	dragging: 'c-TopologicalEditorMapLine--dragging',
	labelSpan: `c-TopologicalEditorMapLine__label--span`,
	labelDropspan: `c-TopologicalEditorMapLine__label--dropSpan`,
	labelVectorAnchor: `c-TopologicalEditorMapLine__label--vectorAnchor`,
	labelAnchor: `c-TopologicalEditorMapLine__label--anchor`,
	labelUnknown: `c-TopologicalEditorMapLine__label--unknown`,
	labelHorizontal: `c-TopologicalEditorMapLine__label--horizontal`,
	labelRaised: `c-TopologicalEditorMapLine__label--raised`,
};

const Styles = {
	[LineCSS.container]: {
		pointerEvents: 'none',
		transform: 'translate3d(0, 0, -1px)',
		transition: 'opacity 150ms ease',

		[`&:hover .${LineCSS.highlight}`]: {
			opacity: 1,
		},
	},
	[LineCSS.clickTarget]: {
		cursor: 'pointer',
		pointerEvents: 'all',
	},
	[LineCSS.highlight]: {
		opacity: 0,
		transition: 'opacity 150ms ease',
		transform: 'translate3d(0, 0, -1px)',
	},
	[`${LineCSS.lineSelected} .${LineCSS.highlight}`]: {
		opacity: 1,
	},
	[LineCSS.label]: {
		cursor: 'pointer',
		position: 'absolute',
		transform: 'translate3d(-50%, -100%, -1px)',
		opacity: 0.75,
	},
	// Hovered/selected label: full opacity and lifted above other labels (within the labels layer).
	[LineCSS.labelRaised]: {
		opacity: 1,
		zIndex: 600,
	},
	[LineCSS.labelText]: {
		padding: '2px 4px',
		cursor: 'pointer',
		pointerEvents: 'all',
		transformOrigin: '50% 50%',
		color: rgba(BLACK),
		fontWeight: 500,
		background: getSpanLineColor('unknown'),
		transition: 'background 150ms ease',
	},
	[LineCSS.labelUnknown]: {
		color: rgba(WHITE),
	},
	[LineCSS.labelHorizontal]: {
		transform: 'translate3d(-50%, calc(-100% - 8px), -1px)',
	},
	[LineCSS.handle]: {
		cursor: 'crosshair',
		userSelect: 'none',
		transform: 'translate3d(0, 0, -1px)',
		pointerEvents: 'all',
	},
	[`${LineCSS.dragging} .${LineCSS.handle}`]: {
		cursor: 'move',
	},
	[LineCSS.labelSpan]: {
		background: getSpanLineColor('span'),
	},
	[LineCSS.labelDropspan]: {
		background: getSpanLineColor('dropspan'),
	},
	[LineCSS.labelVectorAnchor]: {
		background: getSpanLineColor('vector-anchor'),
	},
	[LineCSS.labelAnchor]: {
		background: getSpanLineColor('anchor'),
	},
};

addStyles(Styles);

const SPAN_DISPLAY_NAMES = ['span', 'spans'];

export function getSpanType(field: CollectionModelFieldVector | CollectionModelFieldAnchor): MaybeSpanType {
	// (NEW)     If the parent subform is a span subform and this is the span vector field,
	// (LEGACY)  Or the subform has the title of "span" | "spans"
	//   Then return span related colors
	if (
		(CollectionModelFieldVector.isVectorField(field) &&
			field.schema.parent?.subFormSpanTypeState?.vectorFieldId === field.schema.id) ||
		SPAN_DISPLAY_NAMES.includes(field.parent.schema.displayName.toLowerCase())
	) {
		if (field.provider === CollectionModelFieldVector.PROVIDER_COLLECTION) {
			return 'span';
		}

		return 'dropspan';
	}

	if (
		field.schema.parent?.subFormAnchorState?.leadLengthFieldId === field.schema.id ||
		field.parent.schema.displayName.toLowerCase() === 'anchor'
	) {
		if (CollectionModelFieldAnchor.isAnchorField(field)) {
			return 'anchor';
		}
		return 'vector-anchor';
	}

	if (CollectionModelFieldAnchor.isAnchorField(field)) {
		return 'anchor';
	}

	return 'unknown';
}

export function getSpanLineColor(kind: MaybeSpanType, opacity = 1): string {
	let color;

	switch (kind) {
		case 'span':
			color = ColorPaletteOrange.C400;
			break;

		case 'dropspan':
			color = ColorPaletteYellow.C400;
			break;

		case 'vector-anchor':
		case 'anchor':
			color = ColorPaletteBlue.C400;
			break;

		default:
			color = ColorPaletteGrey.C800;
			break;
	}

	return rgba(color, opacity);
}

/**
 * Vector length for the map label. In metric, a whole-number length drops the trailing `.00`
 * (`12.00m` → `12m`) to save space; other decimals and imperial (feet-inch) output are untouched.
 */
function formatVectorDistance(distance: number): string {
	const units = Services.UserSettings.units;
	const text = IkeUnit.display(distance, units);
	return units === IkeUnit.METER ? text.replace(/\.00m$/, 'm') : text;
}

export class MapLine {
	kind: MaybeSpanType;
	initialPoint: google.maps.Point;
	terminalPoint: google.maps.Point;
	width: number;
	height: number;
	length: number;
	angle: number;
	leftPx: string;

	constructor(private opts: ITopologicalEditorMapLineOptions) {
		this.kind = opts.kind;
		this.terminalPoint = PointCache.getPoint(opts.vector.target.latitude, opts.vector.target.longitude);

		const [topLeft, bottomRight] = this.boundingRect;

		this.width = Math.abs(topLeft.x - bottomRight.x);
		this.height = Math.abs(topLeft.y - bottomRight.y);
		this.length = Math.hypot(this.width, this.height);
		this.angle = Math.atan2(this.terminalPoint.y - opts.initialPoint.y, this.terminalPoint.x - opts.initialPoint.x);
		this.leftPx = `${opts.initialPoint.x}px`;
	}

	get calculatedTerminalPoint() {
		return this.opts.isSelected
			? {
					x: this.terminalPoint.x + this.opts.vectorHandleMoveDelta.x,
					y: this.terminalPoint.y + this.opts.vectorHandleMoveDelta.y,
				}
			: this.terminalPoint;
	}

	get boundingRect() {
		return [
			new google.maps.Point(
				Math.min(this.opts.initialPoint.x, this.calculatedTerminalPoint.x),
				Math.min(this.opts.initialPoint.y, this.calculatedTerminalPoint.y)
			),
			new google.maps.Point(
				Math.max(this.opts.initialPoint.x, this.calculatedTerminalPoint.x),
				Math.max(this.opts.initialPoint.y, this.calculatedTerminalPoint.y)
			),
		];
	}

	get lineDimensions() {
		return {
			x1: this.opts.initialPoint.x,
			y1: this.opts.initialPoint.y,
			x2: this.calculatedTerminalPoint.x,
			y2: this.calculatedTerminalPoint.y,
		};
	}

	get clickTarget() {
		return m('line', {
			className: LineCSS.clickTarget,
			'stroke-width': '12px',
			stroke: 'rgba(0,0,0,0)',
			role: 'button',
			onclick: this.opts.onClick,
			...this.lineDimensions,
		});
	}

	get line() {
		return m('g', [
			m('line', {
				className: LineCSS.highlight,
				stroke: rgba(WHITE),
				'stroke-width': '4px',
				'stroke-dasharray': '2px 2px',
				...this.lineDimensions,
			}),
			m('line', {
				className: LineCSS.line,
				stroke: getSpanLineColor(this.kind),
				'stroke-width': '2px',
				...this.lineDimensions,
			}),
		]);
	}

	get handle() {
		let handleEl = null;

		if (this.opts.isSelected && this.kind !== 'span') {
			handleEl = m('circle', {
				className: LineCSS.handle,
				cx: this.terminalPoint.x + this.opts.vectorHandleMoveDelta.x,
				cy: this.terminalPoint.y + this.opts.vectorHandleMoveDelta.y,
				r: 4,
				fill: rgba(WHITE),
				stroke: rgba(BLACK),
				'stroke-width': '1px',
				onmousedown: this.opts.onVectorHandleMouseDown,
			});
		}

		return handleEl;
	}

	get label() {
		const showLabel = this.opts.showVectorLabel || (this.opts.drawLabels && !this.opts.suppressLabel);
		if (showLabel) {
			let distance = 0;
			let bearing = 0;
			const field = this.opts.vector.field;
			if (CollectionModelFieldAnchor.isAnchorField(field)) {
				const distanceBearingPitch = field.distanceBearingPitch(this.opts.vector.source);
				if (distanceBearingPitch != null) {
					distance = distanceBearingPitch.distance;
					bearing = distanceBearingPitch.bearing;
				}
			} else {
				const newVector = IkeGeodesic.getVector(this.opts.vector.source, this.opts.vector.target);
				distance = newVector.distance;
				bearing = newVector.bearing;
			}

			const [topLeft, bottomRight] = this.boundingRect;
			const lineAngle = Math.atan2(
				this.calculatedTerminalPoint.y - this.opts.initialPoint.y,
				this.calculatedTerminalPoint.x - this.opts.initialPoint.x
			);

			const textAngle = uprightAngle(lineAngle);

			const midPoint = {
				x: (topLeft.x + bottomRight.x) / 2,
				y: (topLeft.y + bottomRight.y) / 2,
			};

			// Anchor labels render horizontal at the vector end so the line stays visible below;
			// spans keep the rotated inline label at the midpoint.
			const isAnchorLabel = this.kind === 'anchor' || this.kind === 'vector-anchor';
			const labelPoint = isAnchorLabel ? this.calculatedTerminalPoint : midPoint;

			return m(
				'div',
				{
					className: classNames(LineCSS.label, {
						[LineCSS.labelHorizontal]: isAnchorLabel,
						[LineCSS.labelRaised]: this.opts.isSelected || this.opts.showVectorLabel,
					}),
					style: {
						left: `${labelPoint.x}px`,
						top: `${labelPoint.y}px`,
					},
				},
				m(
					'div',
					{
						className: classNames(LineCSS.labelText, {
							[LineCSS.labelVectorAnchor]: this.kind === 'vector-anchor',
							[LineCSS.labelAnchor]: this.kind === 'anchor',
							[LineCSS.labelSpan]: this.kind === 'span',
							[LineCSS.labelDropspan]: this.kind === 'dropspan',
							[LineCSS.labelUnknown]: this.kind === 'unknown',
						}),
						style: {
							transform: isAnchorLabel ? 'translateZ(-1px)' : `rotate(${textAngle}rad) translateZ(-1px)`,
						},
						onclick: this.opts.onClick,
						// The label lives in its own layer now, so it must carry its own hover-to-raise.
						onmouseover: this.opts.onVectorMouseOver,
						onmouseout: this.opts.onVectorMouseOut,
					},
					formatVectorDistance(distance) + ` ${IkeUnit.displayAngle(bearing, IkeUnitAngle.DEGREE, undefined, 0)}`
				)
			);
		}

		return null;
	}

	/** The line stroke + interaction handles. The label is rendered separately (see `label`) into a
	 *  layer above all lines so no line ever paints over a label. */
	get lineNodes() {
		return m(
			'div',
			{
				className: classNames(LineCSS.container, {
					[LineCSS.lineSelected]: this.opts.isSelected,
				}),
				onmouseover: this.opts.onVectorMouseOver,
				onmouseout: this.opts.onVectorMouseOut,
			},
			m(
				'svg',
				{
					className: LineCSS.vector,
					style: {
						overflow: 'visible',
						position: 'absolute',
					},
				},
				[this.line, this.clickTarget, this.handle]
			)
		);
	}
}
